import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { APP_INITIALIZER, ModuleWithProviders, NgModule } from '@angular/core';
import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
  MsalGuard,
  MsalInterceptor,
  MsalModule,
  MsalRedirectComponent,
  MsalService,
} from '@azure/msal-angular';
import { BrowserCacheLocation, InteractionType, PublicClientApplication } from '@azure/msal-browser';
import { TranslateService } from '@ngx-translate/core';
import { EntraIdConfig, LOGIN_TYPE, LOGOUT_TYPE } from './entra-id-config';
import * as deTranslations from './i18n/de-lang.json';
import * as enTranslations from './i18n/en-lang.json';
import { API_PREFIX } from './providers/api-prefix.provider';
import { entraIdProviderFactory } from './providers/entra-id.provider';
import { LOGIN_BEHAVIOR } from './providers/login-behavior.provider';
import { LOGOUT_BEHAVIOR } from './providers/logout-behavior.provider';
import { EntraIdAuthRepository } from './repositories/entra-id-auth.repository';
import { EntraIdAuthMock } from './services/entra-id-auth.mock';
import { EntraIdAuthService } from './services/entra-id-auth.service';
import { EntraIdService } from './services/entra-id.service';

const isIE = window.navigator.userAgent.indexOf('MSIE ') > -1 || window.navigator.userAgent.indexOf('Trident/') > -1;

@NgModule({
  imports: [HttpClientModule, MsalModule],
  bootstrap: [MsalRedirectComponent],
  declarations: [],
  exports: [],
})
export class EntraIdAuthModule {
  static forRoot(config: EntraIdConfig, apiPrefix: string): ModuleWithProviders<EntraIdAuthModule> {
    const prefix = apiPrefix.endsWith('/') ? apiPrefix : `${apiPrefix}/`;

    return {
      ngModule: EntraIdAuthModule,
      providers: [
        {
          provide: HTTP_INTERCEPTORS,
          useClass: MsalInterceptor,
          multi: true,
        },
        {
          provide: MSAL_INSTANCE,
          useFactory: () => {
            return new PublicClientApplication({
              auth: {
                clientId: config.clientId,
                authority: config.authority,
                redirectUri: config.redirectUri,
              },
              cache: {
                cacheLocation: BrowserCacheLocation.LocalStorage,
                storeAuthStateInCookie: isIE,
              },
            });
          },
        },
        {
          provide: MSAL_GUARD_CONFIG,
          useFactory: () => {
            return {
              interactionType: InteractionType.Redirect,
              authRequest: {
                scopes: ['user.read'],
              },
            };
          },
        },
        {
          provide: MSAL_INTERCEPTOR_CONFIG,
          useFactory: () => {
            const protectedResourceMap = new Map<string, Array<string>>();
            protectedResourceMap.set('https://graph.microsoft.com/v1.0/me', ['user.read']);
            return { interactionType: InteractionType.Redirect, protectedResourceMap };
          },
        },
        MsalService,
        MsalGuard,
        MsalBroadcastService,
        EntraIdAuthService,
        EntraIdService,
        EntraIdAuthRepository,
        {
          provide: APP_INITIALIZER,
          useFactory: entraIdProviderFactory,
          multi: true,
          deps: [EntraIdService],
        },
        { provide: API_PREFIX, useValue: prefix },
        { provide: LOGIN_BEHAVIOR, useValue: config.loginType ?? LOGIN_TYPE.REDIRECT },
        { provide: LOGOUT_BEHAVIOR, useValue: config.logoutType ?? LOGOUT_TYPE.REDIRECT },
      ],
    };
  }

  static forTest(): ModuleWithProviders<EntraIdAuthModule> {
    return {
      ngModule: EntraIdAuthModule,
      providers: [
        {
          provide: EntraIdAuthService,
          useClass: EntraIdAuthMock,
        },
      ],
    };
  }

  constructor(private translate: TranslateService) {
    this.translate.setTranslation('en', enTranslations, true);
    this.translate.setTranslation('de', deTranslations, true);
  }
}
